/* GENERATED FILE — DO NOT EDIT.
 *
 * The canonical muscle vocabulary, emitted from the enum the phone compiles:
 *   HybridTrainer/Services/AI/MuscleNormalizer.swift  (`CanonicalMuscle`)
 *
 * Regenerate: compile that file with a two-line `main.swift` printing
 * `rawValue \t displayName \t group` for `CanonicalMuscle.allCases`, and paste its output here.
 * ✅ **THE RULER EXISTS — `musclesGenerated.test.ts` (2026-09-21), 8 rulers, 8 mutation arms, all
 * caught.** It reads THIS file and `MuscleNormalizer.swift` and compares the cases, the order, the
 * labels, the groups and the declared count. ⚠️ **It landed in VITEST rather than the Swift
 * harness, which is a change of mind with a reason**: the harness compiles `main.swift` before it
 * judges anything, so this question would have cost ~12-18 minutes to ask and could only have been
 * mutation-tested in hours; here it costs milliseconds and its arms cost seconds. A check that is
 * cheap to run is a check that actually runs. ⚠️ **It was GREEN the day it landed** — the two
 * sides already agreed — so its whole value is the day somebody adds a 25th `CanonicalMuscle` and
 * the web's editor quietly stops offering it. Nothing else in this repo would go red that day.
 *
 * ── WHY IT EXISTS (MEASURED 2026-09-18) ──────────────────────────────────────────────────────
 *
 * 🔴 `coach_exercises.primary_muscles` is FREE TEXT and already carries **39 distinct spellings**
 * for about twenty muscles: `shoulders` 33 / `Shoulders` 4 · `quadriceps` 14 / `Quads` 2 /
 * `quads` 1 · `midBack` 25 / `Mid Back` 2 · `core` 28 / `Core` 2 · `triceps` 28 / `Triceps` 2 ·
 * `chest` 12 / `Chest` 1 / `pectoralis major` 11 · `hipGroin` 3 / `Hip & Groin` 3.
 *
 * That is this repo's defining bug class, live in the data. `MuscleNormalizer` exists to cope with
 * it at READ time — but every new free-typed spelling is another row whose muscles the relatedness
 * score and the coverage reads see as nothing, with NOTHING RED. The web's editor suggests from
 * this list so the drift stops growing; it does not forbid a coach's own word, it marks it.
 */

export type CanonicalMuscle = {
  /** The enum's raw value — what a new row should store. */
  readonly key: string;
  /** What a coach reads. */
  readonly label: string;
  readonly group: string;
};

export const CANONICAL_MUSCLES: readonly CanonicalMuscle[] = [
  { key: "quadriceps", label: "Quadriceps", group: "legs" },
  { key: "hamstrings", label: "Hamstrings", group: "legs" },
  { key: "glutes", label: "Glutes", group: "legs" },
  { key: "adductors", label: "Adductors", group: "legs" },
  { key: "abductors", label: "Abductors", group: "legs" },
  { key: "calves", label: "Calves", group: "legs" },
  { key: "hipFlexors", label: "Hip Flexors", group: "legs" },
  { key: "latissimusDorsi", label: "Lats", group: "back" },
  { key: "trapezius", label: "Traps", group: "back" },
  { key: "rhomboids", label: "Rhomboids", group: "back" },
  { key: "erectorSpinae", label: "Lower Back", group: "back" },
  { key: "pectoralisMajor", label: "Chest", group: "chest" },
  { key: "serratusAnterior", label: "Serratus", group: "chest" },
  { key: "anteriorDeltoid", label: "Front Delts", group: "shoulders" },
  { key: "lateralDeltoid", label: "Side Delts", group: "shoulders" },
  { key: "posteriorDeltoid", label: "Rear Delts", group: "shoulders" },
  { key: "rotatorCuff", label: "Rotator Cuff", group: "shoulders" },
  { key: "biceps", label: "Biceps", group: "arms" },
  { key: "triceps", label: "Triceps", group: "arms" },
  { key: "forearms", label: "Forearms", group: "arms" },
  { key: "rectusAbdominis", label: "Abs", group: "core" },
  { key: "obliques", label: "Obliques", group: "core" },
  { key: "transverseAbdominis", label: "Deep Core", group: "core" },
  { key: "neck", label: "Neck", group: "shoulders" },
];

export const CANONICAL_MUSCLE_COUNT = 24;

/** Is this stored string one the engine's readers will recognise?
 *
 * ⚠️ Compared case-INSENSITIVELY against both the key and the label, because both spellings are
 * live in the column today and neither is wrong — what is wrong is a third one nobody can read.
 */
export function isCanonicalMuscle(raw: string): boolean {
  const t = raw.trim().toLowerCase();
  return CANONICAL_MUSCLES.some((m) => m.key.toLowerCase() === t || m.label.toLowerCase() === t);
}
